import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, UserX, Clock } from "lucide-react";

const PendingAdminsPage = () => {
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingAdmins();
  }, []);

  const fetchPendingAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-admins`);
      setPendingAdmins(response.data);
    } catch (error) {
      toast.error("Failed to fetch pending admins");
    } finally {
      setLoading(false);
    }
  };

  const approveAdmin = async (userId, email) => {
    try {
      await axios.put(`${API}/admin/approve-admin/${userId}`);
      toast.success(`${email} approved as admin`);
      fetchPendingAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve admin");
    }
  };

  const rejectAdmin = async (userId, email) => {
    if (!window.confirm(`Reject and remove ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/reject-admin/${userId}`);
      toast.success(`${email} rejected`);
      fetchPendingAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject admin");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pending-admins-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          Admin Approvals
        </h1>
        <p className="text-text-secondary mt-1">
          Approve or reject pending admin registrations
        </p>
      </div>

      <Card className="border-primary/20">
        <CardHeader className="py-3 bg-secondary/30">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-status-warning" />
            Pending Requests ({pendingAdmins.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {pendingAdmins.length === 0 ? (
            <div className="p-8 text-center text-text-secondary">
              No pending admin requests
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/20">
                  <TableHead className="font-bold text-text-primary">Name</TableHead>
                  <TableHead className="font-bold text-text-primary">Email</TableHead>
                  <TableHead className="font-bold text-text-primary">Registered</TableHead>
                  <TableHead className="font-bold text-text-primary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingAdmins.map((admin) => (
                  <TableRow key={admin.id} className="hover:bg-surface-muted" data-testid={`pending-admin-${admin.id}`}>
                    <TableCell className="font-medium">
                      {admin.first_name} {admin.last_name}
                    </TableCell>
                    <TableCell className="text-text-secondary">{admin.email}</TableCell>
                    <TableCell className="text-text-secondary text-sm">
                      {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => approveAdmin(admin.id, admin.email)}
                          className="bg-status-success hover:bg-status-success/90 text-white"
                          data-testid={`approve-${admin.id}`}
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rejectAdmin(admin.id, admin.email)}
                          className="text-status-error border-status-error hover:bg-status-error/10"
                          data-testid={`reject-${admin.id}`}
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingAdminsPage;
